/**
 * Custom hooks for Visions Special state management
 * Centralizes state logic for better organization and reusability
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '@/lib/utils/logger';

/**
 * Hook for managing fullscreen functionality
 */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showZoomHint, setShowZoomHint] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          setIsFullscreen(true);
          setShowZoomHint(true);
          document.body.classList.add('full-screen');
          setTimeout(() => {
            setShowZoomHint(false);
          }, 3000);
        })
        .catch(err => {
          logger.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
    } else {
      if (document.exitFullscreen) {
        document.body.classList.remove('full-screen');
        document
          .exitFullscreen()
          .then(() => {
            setIsFullscreen(false);
          })
          .catch(err => {
            logger.error(`Error attempting to exit fullscreen: ${err.message}`);
          });
      }
    }
  }, []);

  const openFullscreenImage = useCallback((src: string) => {
    setFullscreenImage(src);
    document.body.classList.add('full-screen');
  }, []);

  const closeFullscreenImage = useCallback(() => {
    setFullscreenImage(null);
    document.body.classList.remove('full-screen');
  }, []);

  // Monitor fullscreen changes from other sources (like Esc key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) {
        document.body.classList.remove('full-screen');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.body.classList.remove('full-screen');
    };
  }, []);

  return {
    isFullscreen,
    showZoomHint,
    fullscreenImage,
    toggleFullscreen,
    openFullscreenImage,
    closeFullscreenImage
  };
}

/**
 * Hook for managing gallery/show all section
 */
export function useGallerySection() {
  const [showAllSection, setShowAllSection] = useState(false);

  const handleShowAll = useCallback(() => {
    setShowAllSection(true);

    // Smooth scroll to the section
    setTimeout(() => {
      const section = document.getElementById('show-all-section');
      const scrollContainer = document.getElementById('landscape-content');
      if (section && scrollContainer) {
        const sectionRect = section.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollTop = sectionRect.top - containerRect.top + scrollContainer.scrollTop;

        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, []);

  const closeShowAll = useCallback(() => {
    setShowAllSection(false);
  }, []);

  return {
    showAllSection,
    setShowAllSection,
    handleShowAll,
    closeShowAll
  };
}

/**
 * Hook for keyboard navigation
 */
export function useKeyboardNavigation(
  onLeft?: () => void,
  onRight?: () => void,
  onFullscreen?: () => void
) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          if (onLeft) {
            e.preventDefault();
            e.stopPropagation();
            onLeft();
            // Focus canvas element if exists
            const canvasElement = document.querySelector('canvas');
            if (canvasElement) canvasElement.focus();
          }
          break;
        case 'ArrowRight':
          if (onRight) {
            e.preventDefault();
            e.stopPropagation();
            onRight();
            const canvasElement = document.querySelector('canvas');
            if (canvasElement) canvasElement.focus();
          }
          break;
        case 'f':
        case 'F':
          if (onFullscreen) {
            e.preventDefault();
            e.stopPropagation();
            onFullscreen();
            const canvasElement = document.querySelector('canvas');
            if (canvasElement) canvasElement.focus();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onLeft, onRight, onFullscreen]);
}

/**
 * Hook for footer visibility management
 */
export function useFooterVisibility(shouldHide: boolean = false) {
  useEffect(() => {
    if (!shouldHide) return undefined;

    const setShowFooter = (show: boolean) => {
      // This would need to be connected to your footer context
      // For now, it's a placeholder
      const event = new CustomEvent('footerVisibilityChange', { detail: show });
      window.dispatchEvent(event);
    };

    setShowFooter(false);

    return () => {
      setShowFooter(true);
    };
  }, [shouldHide]);
}

/**
 * Main hook combining all Visions Special state management
 */
export function useVisionsState(layout: any) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const videoRef = useCallback((node: HTMLVideoElement | null) => {
    if (node) {
      node.addEventListener('loadeddata', () => setIsVideoLoading(false));
      return () => node.removeEventListener('loadeddata', () => setIsVideoLoading(false));
    }
    return undefined;
  }, []);

  const { isFullscreen, showZoomHint, fullscreenImage, toggleFullscreen, openFullscreenImage } = useFullscreen();
  const { showAllSection, setShowAllSection } = useGallerySection();

  const artImages = layout?.artImages || [];
  const visionsVideos = useMemo(() => layout?.visionsVideos || [], [layout?.visionsVideos]);

  const handleRandomVideo = useCallback(() => {
    if (visionsVideos.length === 0) return;
    setIsChanging(true);
    setIsVideoLoading(true);
    const randomIndex = Math.floor(Math.random() * visionsVideos.length);
    setCurrentVideoIndex(randomIndex);
    setTimeout(() => setIsChanging(false), 500);
  }, [visionsVideos]);

  return {
    artImages,
    visionsVideos,
    currentImageIndex,
    fullscreenImage,
    showAllSection,
    currentVideoIndex,
    isVideoLoading,
    isChanging,
    isFullscreen,
    showZoomHint,
    videoRef,
    setCurrentImageIndex,
    setFullscreenImage: openFullscreenImage,
    setShowAllSection,
    handleRandomVideo,
    toggleFullscreen,
  };
}
